import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObs } from '../../contexts/ObsContext';
import { useAuth } from '../../contexts/AuthContext';
import { canObserve } from '../../lib/permissions';
import { Btn } from '../../components/ui';
import { toast } from 'react-toastify';
import { feedbackAPI } from '../../api';
import useRequireObs from '../../lib/useRequireObs';
import { BUCKETS } from '../../theme';

/**
 * The greeting and sign-off adapt to how the conversation ended. A teacher who
 * wanted time gets "there is no rush"; one who took the feedback without a
 * conversation is not asked to reply.
 */
function emailVoice(outcome, hasAgreedStep) {
  const tail = hasAgreedStep ? ' What you decided to try for them is at the end.' : '';
  switch (outcome) {
    case 'later':
      return {
        opening: 'Thank you again for having me in your room. Below are the things I ' +
                 'noticed about what your students were doing and learning, in the same ' +
                 'words I used when we spoke. There is no rush with any of it — come back ' +
                 'to it whenever you are ready.',
        signoff: 'If you would like to talk it through at some point, tell me when and I will make the time.',
      };
    case 'none':
      return {
        opening: 'Thank you again for having me in your room. Below are the things I ' +
                 'noticed about what your students were doing and learning, in the same ' +
                 'words I used when we spoke, so you have them in writing.',
        signoff: 'No reply needed. These are yours to use however is useful.',
      };
    default: // 'now'
      return {
        opening: 'Thank you again for having me in your room. Below are the things I ' +
                 'noticed about what your students were doing and learning, in the same ' +
                 'words I used when we spoke.' + tail,
        signoff: 'Thanks again for thinking it through with me.',
      };
  }
}

/**
 * One statement in the email — identical to what was read aloud, colour coded
 * by direction, with its bucket tag.
 */
function EmailStatement({ card }) {
  const neg = card.side === 'neg';
  const bucket = BUCKETS[card.bucket];
  return (
    <div className={`email-stmt ${neg ? 'neg' : 'pos'}`}>
      <p>{card.statement}</p>
      {bucket && (
        <span className={`email-bucket ${neg ? 'neg' : 'pos'}`}>{bucket.name}</span>
      )}
    </div>
  );
}

/** A section heading with a rule running off to the right. */
function EmailSection({ children }) {
  return <div className="email-sec"><span>{children}</span><i /></div>;
}

const longDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d) ? '' : d.toLocaleDateString('en-US',
    { weekday: 'long', month: 'long', day: 'numeric' });
};

/* ── Feedback Send ────────────────────────────────────────────────────────── */
export function FeedbackSendPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { obs, teacher, finalBest, finalBetter, nextStep, closeOutcome } = useObs();
  useRequireObs();
  const [loading, setLoading] = useState(false);

  const teacherName  = teacher?.display_name || teacher?.full_name || 'the teacher';
  const teacherFirst = teacher?.first_name || (teacher?.full_name || '').split(' ')[0] || 'there';
  const observer     = user?.full_name || 'your observer';
  const observerFirst = user?.first_name || observer.split(' ')[0];

  const outcome = closeOutcome || 'now';

  // The agreed step block only appears if a step was actually agreed.
  const hasAgreedStep = outcome === 'now' && !!nextStep.trim();
  const voice = emailVoice(outcome, hasAgreedStep);

  const handleSend = async () => {
    setLoading(true);
    try {
      await feedbackAPI.send({
        observation_id:    obs?.id,
        best_for:          finalBest,
        better_for:        finalBetter,
        close_outcome:     outcome,
        agreed_next_step:  hasAgreedStep ? nextStep : '',
        edited_before_send: false,
      });
      navigate('/observe/sent');
    } catch (error) {
      console.error(error);
      toast.error('Could not send the feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen">
      <div className="obsbar">
        <button className="obsbar-back" onClick={() => navigate('/observe/debrief')}>← Back</button>
        <div className="obsbar-title">Feedback email</div>
      </div>

      <div className="scroll">
        <div className="pad">
          <div className="email-wrap">
            {/* Envelope */}
            <div className="email-env">
              <div className="email-env-row">
                <span>From</span>
                <span>{observer} · ELO Insights</span>
              </div>
              <div className="email-env-row">
                <span>To</span>
                <span>{teacherName}{obs?.school_name ? ` · ${obs.school_name}` : ''}</span>
              </div>
              <div className="email-env-row">
                <span>Subject</span>
                <strong>What I saw students learning in your {obs?.subject} lesson</strong>
              </div>
            </div>

            {/* Masthead */}
            <div className="email-head">
              <div className="email-brand">
                <span className="email-mark">
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="6" stroke="#fff" strokeWidth="1.8"/>
                    <path d="M10 7v4l2.5 1.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                </span>
                <span>
                  <span className="email-brand-name">ELO Insights</span>
                  <span className="email-brand-sub">Whitehead Ross Education &amp; Consulting</span>
                </span>
              </div>
              <div className="email-title">What the students were learning</div>
              <div className="email-meta">
                {[obs?.grade_level, obs?.subject, longDate(obs?.observation_date)]
                  .filter(Boolean).join(' · ')} · observed by {observer}
              </div>
            </div>

            {/* Body */}
            <div className="email-body">
              <p className="email-hi">Hi {teacherFirst},</p>
              <p className="email-para">{voice.opening}</p>

              {finalBest.length > 0 && (
                <>
                  <EmailSection>Where learning was most effective</EmailSection>
                  {finalBest.map((card, i) => <EmailStatement key={card.id || i} card={card} />)}
                </>
              )}

              {finalBetter.length > 0 && (
                <>
                  <EmailSection>Where learning could have been more effective</EmailSection>
                  {finalBetter.map((card, i) => <EmailStatement key={card.id || i} card={card} />)}
                </>
              )}

              {hasAgreedStep && (
                <>
                  <EmailSection>What you decided to try for these students</EmailSection>
                  <div className="email-step">
                    <div className="email-step-lbl">Agreed together</div>
                    <p>{nextStep}</p>
                  </div>
                </>
              )}

              <p className="email-para">{voice.signoff}</p>
              <p className="email-sign">{observerFirst}</p>
            </div>

            {/* Footer */}
            <div className="email-footer">
              <strong>These notes are yours.</strong> They are not an evaluation and they are
              not shared with your leadership team. Student names appear here so you know who
              I meant, and they are removed from every dashboard and report.
            </div>
          </div>

          <Btn variant="blue" onClick={handleSend} loading={loading}
               style={{ marginTop: 14, marginBottom: 8 }}>
            Send to teacher
          </Btn>
          <Btn variant="outline" onClick={() => navigate('/observe/debrief')}>
            Back and edit
          </Btn>
        </div>
      </div>
    </div>
  );
}

/* ── Feedback Sent (journey complete) ─────────────────────────────────────── */
export function FeedbackSentPage() {
  const navigate = useNavigate();
  const { teacher, resetObs } = useObs();
  const { user } = useAuth();
  const teacherName = teacher?.full_name || 'the teacher';

  const handleHome = () => { resetObs(); navigate('/'); };
  const handleAnother = () => { resetObs(); navigate('/observe/context'); };

  return (
    <div className="screen" style={{ background:'var(--surface)' }}>
      <div className="scroll">
        <div className="pad" style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100%', textAlign:'center' }}>
          {/* Tick */}
          <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--blue-l)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M5 14l6 6 12-12" stroke="var(--blue)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <div style={{ fontSize:22, fontWeight:600, color:'var(--text)', letterSpacing:'-0.02em', marginBottom:8 }}>
            Observation complete
          </div>
          <div style={{ fontSize:14, color:'var(--text2)', lineHeight:1.6, marginBottom:32 }}>
            Feedback sent to {teacherName}. Saved to your history across all schools.
          </div>

          {/* Journey dots */}
          <div style={{ background:'var(--white)', borderRadius:'var(--r)', padding:14, width:'100%', maxWidth:320, marginBottom:24, border:'0.5px solid var(--border)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              {[{n:1,l:'Observed'},{n:2,l:'Debriefed'},{n:3,l:'Sent'}].map((s,i) => (
                <span key={i} style={{ display:'flex', alignItems:'center', gap:4 }}>
                  {i > 0 && <span style={{ width:20, height:1.5, background:'var(--blue)' }} />}
                  <span style={{ width:22, height:22, borderRadius:'50%', background:'var(--blue)', color:'var(--nav)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700 }}>{s.n}</span>
                  <span style={{ fontSize:11, color:'var(--blue-d)', fontWeight:500 }}>{s.l}</span>
                </span>
              ))}
            </div>
          </div>

          <div style={{ width:'100%', maxWidth:320 }}>
            <Btn variant="blue" onClick={handleHome} style={{ marginBottom:8 }}>Back to home</Btn>
            {canObserve(user) && (
              <Btn variant="outline" onClick={handleAnother}>Start another observation</Btn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
